/**
 * Enregistrement d'une visite (§9) — le côté navigateur de la boucle virale.
 *
 * ══ Ce que ce module fabrique, et ce qu'il ne fabrique surtout pas ═══════════
 *
 * Il produit une **empreinte d'appareil**, la hache, et l'envoie à `register-visit`.
 * Cette empreinte sert à une seule chose : compter une visite par personne, par ville et
 * par jour. Elle n'identifie personne, ne survit pas à un effacement du stockage local,
 * et ne quitte jamais le navigateur en clair.
 *
 * ══ Pourquoi un identifiant TIRÉ AU SORT plutôt qu'un « fingerprint » ═══════
 *
 * La tentation était de composer une signature à partir de l'appareil — résolution,
 * fuseau, polices, moteur de rendu. C'est ce que fait le pistage publicitaire, et c'est
 * précisément ce que le §13 refuse : une telle signature suit une personne d'un site à
 * l'autre, sans son accord et sans qu'elle puisse s'en défaire.
 *
 * On tire donc un identifiant **au hasard**, une seule fois, et on le range dans le
 * stockage local. Il ne dit rien de l'appareil, ne se recoupe avec rien, et le joueur
 * s'en débarrasse en vidant son navigateur. Il est *moins* robuste qu'une empreinte
 * matérielle — quelqu'un de motivé le contourne en navigation privée — et c'est un
 * compromis assumé : le plafond de dix boosts actifs (§9) limite déjà ce qu'un abus
 * rapporte, et aucun anti-abus ne vaut de pister ses joueurs.
 *
 * ══ Le hachage est fait ICI, et re-salé côté serveur ════════════════════════
 *
 * Le §9 impose que « seul le hash transite ». Le serveur le re-hache avec un sel qu'il
 * est seul à connaître : une base exfiltrée ne permet donc ni de reconnaître un
 * visiteur, ni de rejouer sa visite depuis ailleurs.
 *
 * ══ Tout échoue en silence ══════════════════════════════════════════════════
 *
 * Un visiteur ne doit jamais voir une erreur parce qu'un compteur n'a pas pu s'incrémenter.
 * La page publique est une vitrine (E§5) : si l'enregistrement échoue, la ville s'affiche
 * quand même, et c'est la seule chose qui compte pour lui.
 */

import { getSupabase, isSupabaseConfigured } from '@/net/supabase';

/** Clé du jeton de visiteur. Distincte de la sauvegarde et de la session d'auth. */
const VISITOR_KEY = 'villopia:visitor:v1';

export interface VisitOutcome {
  /** La visite a-t-elle été comptée ? Faux si déjà venue aujourd'hui, ou en cas d'échec. */
  readonly registered: boolean;
  /** Le propriétaire a-t-il reçu un boost ? Faux si le plafond de dix est atteint. */
  readonly boosted: boolean;
}

const SILENT: VisitOutcome = { registered: false, boosted: false };

/**
 * Jeton de visiteur, stable tant que le navigateur garde son stockage.
 *
 * `crypto.randomUUID` et non un horodatage : deux visiteurs ouvrant le même lien à la
 * même milliseconde doivent compter pour deux, et un identifiant devinable permettrait
 * d'usurper la visite d'autrui — donc de consommer sa place dans l'unicité du jour.
 */
function visitorToken(): string {
  try {
    const existing = localStorage.getItem(VISITOR_KEY);
    if (existing !== null && existing.length >= 16) return existing;

    const fresh = globalThis.crypto.randomUUID();
    localStorage.setItem(VISITOR_KEY, fresh);
    return fresh;
  } catch {
    /*
      Stockage indisponible — navigation privée, politique d'entreprise, contexte non
      sécurisé. On rend un jeton éphémère plutôt que rien : la visite comptera, et le
      plafond de dix boosts empêche que « une visite par rechargement » devienne un abus
      rentable. Refuser de compter serait pire — un visiteur légitime ne serait jamais
      compté sur ces navigateurs.
    */
    return globalThis.crypto.randomUUID();
  }
}

/** SHA-256 hexadécimal. `crypto.subtle` n'existe qu'en contexte sécurisé — d'où le repli. */
async function sha256(value: string): Promise<string | null> {
  try {
    const bytes = new TextEncoder().encode(value);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

export interface RegisterVisitOptions {
  /** Slug de la ville visitée, lu dans l'URL. */
  readonly slug: string;
  /**
   * Slug de la ville du VISITEUR, s'il en a une.
   *
   * C'est ce qui permet au serveur d'écarter une auto-visite sans exiger de compte : la
   * page publique doit rester ouverte à tous, et demander une authentification pour
   * visiter tuerait la boucle avant qu'elle ne commence.
   */
  readonly visitorSlug?: string;
}

export async function registerVisit(options: RegisterVisitOptions): Promise<VisitOutcome> {
  if (!isSupabaseConfigured() || options.slug === '') return SILENT;

  const fingerprint = await sha256(visitorToken());
  if (fingerprint === null) return SILENT;

  try {
    /*
      La réponse est typée `unknown` et non `any`.

      `functions.invoke` rend `any` — donc le compilateur cesse de vérifier quoi que ce
      soit à partir d'ici, sur des données qui viennent du RÉSEAU. C'est exactement la
      même règle que pour une sauvegarde relue : ce qu'on n'a pas produit soi-même se
      revalide champ par champ.
    */
    const response: { data: unknown; error: unknown } = await getSupabase().functions.invoke(
      'register-visit',
      {
        body: {
          slug: options.slug,
          fingerprint,
          ...(options.visitorSlug === undefined ? {} : { visitorSlug: options.visitorSlug }),
        },
      },
    );

    if (response.error !== null || typeof response.data !== 'object' || response.data === null) {
      return SILENT;
    }

    const body = response.data as { registered?: unknown; boosted?: unknown };
    return {
      registered: body.registered === true,
      boosted: body.boosted === true,
    };
  } catch {
    // Hors ligne, fonction indisponible, réponse illisible : la ville s'affiche quand
    // même. Un visiteur ne doit jamais voir une erreur parce qu'un compteur a échoué.
    return SILENT;
  }
}

/**
 * Multiplicateur de boost actif d'une ville, lu depuis la table publique `boosts`.
 *
 * ── Pourquoi le PRODUIT et non la somme ─────────────────────────────────────
 *
 * Deux boosts de ×2 donnent ×4, pas ×3 : c'est ce que « +100 % de production » veut dire
 * quand on l'applique deux fois. Le plafond de dix boosts actifs (§9) borne donc le
 * total, et c'est ce plafond qui fait le travail — pas une formule ad hoc.
 *
 * ── Pourquoi c'est le SERVEUR qui fait foi ──────────────────────────────────
 *
 * Le boost naît d'une visite, donc d'un fait qui s'est produit chez quelqu'un d'autre. Le
 * client ne peut pas l'inventer : `boosts` est en lecture publique et en écriture
 * strictement `service_role`. Un joueur qui éditerait sa sauvegarde locale ne se
 * fabriquerait aucun boost.
 */
export interface BoostState {
  readonly multiplier: number;
  /**
   * Fin du DERNIER boost actif, en millisecondes epoch — 0 sans boost.
   *
   * Le compte à rebours du HUD (demande de Benoit) affiche cette borne : avec
   * plusieurs boosts, le multiplicateur redescend par paliers avant elle, mais
   * « jusqu'à quand suis-je boosté » est la question que le joueur pose.
   */
  readonly until: number;
}

export async function activeBoost(cityId: string, now: number): Promise<BoostState> {
  const none: BoostState = { multiplier: 1, until: 0 };
  if (!isSupabaseConfigured() || cityId === '') return none;

  try {
    const { data, error } = await getSupabase()
      .from('boosts')
      .select('multiplier, expires_at')
      .eq('city_id', cityId)
      .gt('expires_at', new Date(now).toISOString());

    if (error !== null) return none;

    /*
      Chaque multiplicateur est borné entre 1 et 100 — les mêmes bornes que la contrainte
      `boosts_multiplier_range` de la base.

      Le serveur les garantit déjà, et c'est justement pour ça qu'on les répète : une
      valeur aberrante qui franchirait la base ne doit pas devenir une production infinie
      chez le joueur. La défense en profondeur ne coûte ici qu'un `Math.min`.
    */
    let product = 1;
    let until = 0;
    for (const row of data) {
      product *= Math.min(100, Math.max(1, row.multiplier));
      until = Math.max(until, new Date(row.expires_at).getTime());
    }
    return { multiplier: product, until };
  } catch {
    return none;
  }
}

/**
 * Nombre total de visites reçues par une ville, depuis le SERVEUR.
 *
 * ══ Pourquoi cette fonction a manqué, et ce que son absence a emporté ════════
 *
 * `visitorsReceived` existait dans le snapshot, le HUD l'affichait, les jalons et les
 * quêtes le lisaient — et **aucune ligne du dépôt ne l'écrivait jamais**. Le compteur
 * restait donc à zéro pour l'éternité, et le défaut ne s'arrêtait pas à un chiffre figé :
 *
 * - les **quarante paliers de visiteurs** (`MILESTONE_REWARDS.visitorTiers`, 3 gemmes
 *   chacun) ne se déclenchaient jamais ;
 * - les **quêtes de visite** ne progressaient pas ;
 * - les **traces physiques des visiteurs** (§16.1) — la fleur par visiteur, le banc à 25,
 *   la fontaine à 100 — n'apparaissaient jamais.
 *
 * Toute la contrepartie du partage était morte, sauf le boost. Et c'est précisément
 * pour ça que le défaut a survécu à la sonde `viral.probe.mjs` : le boost, lui, se lit
 * en direct dans `boosts`, donc **la moitié visible de la boucle fonctionnait**.
 *
 * ══ Pourquoi le serveur, et pourquoi `daily_stats` ══════════════════════════
 *
 * Le nombre de visites est un fait qui s'est produit chez quelqu'un d'autre : le client
 * ne peut ni l'observer ni l'inventer honnêtement. `visits` lui est **interdite en
 * lecture** — elle contient une empreinte pseudonyme de visiteur, et sa RLS n'a
 * volontairement aucune policy. `daily_stats` est l'agrégat prévu pour ça : lisible par
 * le seul propriétaire, sans aucune donnée personnelle, écrite par l'Edge Function.
 *
 * La somme et non le dernier jour : le compteur du HUD est un cumul de partie, et les
 * paliers de jalons comptent depuis toujours. Une ville reçoit au plus une ligne par
 * jour — la requête reste minuscule même après des années.
 */
export async function visitsReceived(cityId: string): Promise<number> {
  if (!isSupabaseConfigured() || cityId === '') return 0;

  try {
    const { data, error } = await getSupabase()
      .from('daily_stats')
      .select('visits')
      .eq('city_id', cityId);

    if (error !== null) return 0;

    let total = 0;
    for (const row of data) total += Math.max(0, row.visits);
    return total;
  } catch {
    return 0;
  }
}
